import styled, { css } from 'styled-components';
import {
  Button,
  Divider,
  Icon,
  Link,
  styleMixinInvertedFocusOutline,
  TextButton,
} from '@amzn/storm-ui';
import { LinkButton, NavLink } from '../styles';
import { squidInk } from 'src/colors';

const focusNavElementMixin = css`
  &:focus,
  &:focus-visible {
    ${styleMixinInvertedFocusOutline};
  }
`;

const navLinkIconMixin = css`
  & img,
  i {
    height: 24px;
    width: 24px;
    margin-inline-end: ${({ theme }) => theme.spacing.xsmall.default};
  }
`;

export const MobileNavWrapper = styled('div')`
  box-sizing: border-box;
  display: block;
  min-height: 500px;
  height: 100%;
  overflow-y: auto;
  padding: 0
    ${({ theme }) =>
      `${theme.spacing.xsmall.default} ${theme.spacing.xsmall.default}`};
  background-color: ${squidInk};
`;

export const MobileNavHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xsmall.default} 0;
`;

export const SecondaryMenuWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.xsmall.default};
`;

export const MobileMenuCloseBtn = styled.button`
  color: #fff;
  cursor: pointer;
  border: none;
  background: none;
  user-select: none;
  margin-inline-end: ${({ theme }) => theme.spacing.xsmall.default};
  width: 40px;
  height: 40px;
  padding: 0;
  ${focusNavElementMixin};
`;

export const MobileLogoWrapper = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing.xxxsmall.default};
  border-radius: ${({ theme }) => theme.border.radius.small};

  ${focusNavElementMixin};
`;

export const MobileLogo = styled.img`
  width: 100px;
  vertical-align: bottom;
`;

export const IA2MobileUserIcon = styled(Icon)`
  padding: 1px 2px;
`;

const mobileDividerMixin = css`
  margin: ${({ theme }) => theme.spacing.xsmall.default} 0;
`;

export const MobileDivider = styled(Divider)`
  border-bottom: 2px solid ${({ theme }) => theme.color.gray[900]};
  ${mobileDividerMixin}
`;

export const InvertedMobileDivider = styled(Divider)`
  border-bottom: 2px solid rgb(230, 233, 237);
  ${mobileDividerMixin}
`;

export interface NonExpandableItemProps {
  isSelected: boolean | undefined;
}

export const NonExpandableItemLink = styled(NavLink)<NonExpandableItemProps>`
  border-radius: 5px;
  display: flex;
  padding: ${({ theme }) => theme.spacing.xsmall.default};
  align-items: center;
  ${focusNavElementMixin};
  ${navLinkIconMixin};

  &&&& {
    text-decoration: ${({ theme }) => theme.nav.link.textDecoration};

    &:visited {
      text-decoration: ${({ theme }) => theme.nav.link.textDecoration};
    }
  }

  ${(props) =>
    props.isSelected &&
    `
    background-color: rgb(73, 85, 102);
    font-weight: bold;
    color: ${({ theme }) => theme.color.gray[0]};
  `};
`;

export const MobileSideNavItemText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-family: 'Amazon Ember', Arial, Helvetica, sans-serif;
  font-size: 15px;
  text-align: start;
`;

export interface ExpandableListProps {
  isExpanded: boolean | undefined;
}

export const ExpandableItemWrapper = styled('div')<ExpandableListProps>`
  ${({ isExpanded, theme }) =>
    isExpanded
      ? `
    margin-block-end: ${theme.spacing.xsmall.default};
    background-color: rgb(53, 65, 82);
    box-shadow: 0 2px 4px -2px rgb(0 0 0 / 20%);
    border-radius: 5px;
  `
      : ''};
`;

export const ExpandableItemBtn = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.xsmall.default};
  color: rgb(242, 244, 246);
  border-radius: ${({ theme }) => theme.border.radius.small};

  ${navLinkIconMixin};
  ${focusNavElementMixin};
`;

export const MobileDropdownIcon = styled(Icon)`
  font-size: 15px;
  margin-inline-start: auto;
`;

export const ExpandedListWrapper = styled('div')`
  margin-inline: ${({ theme }) => theme.spacing.xsmall.default};
  font-size: 15px;
  padding-block-end: ${({ theme }) => theme.spacing.xsmall.default};
  transition:
    max-height 0.25s var(--ease-curve),
    opacity 0.25s var(--ease-curve),
    padding 0.1s var(--ease-curve);
`;

export interface ExpandedListItemProps {
  isSelected: boolean | undefined;
}

export const ExpandedListItem = styled(NavLink)<ExpandedListItemProps>`
  display: flex;
  padding-top: 11px;
  padding-bottom: 11px;
  padding-inline: ${({ theme }) => theme.spacing.xsmall.default};
  align-items: center;
  justify-content: space-between;
  border-radius: ${({ theme }) => theme.border.radius.small};
  ${(props) =>
    props.isSelected
      ? `
    background-color: rgb(92, 104, 124);
    font-weight: bold;
    color: ${({ theme }) => theme.color.gray[0]};
  `
      : ''};

  &&&& {
    text-decoration: ${({ theme }) => theme.nav.link.textDecoration};

    &:visited {
      text-decoration: ${({ theme }) => theme.nav.link.textDecoration};
    }
  }
`;

export const UserPreferenceButtonLink = styled(LinkButton)`
  border-radius: 3px;
  border: 1px solid #e7e7e7;
  display: flex;
  padding: ${({ theme }) => theme.spacing.xsmall.default}
    ${({ theme }) => theme.spacing.small.default};
  margin-block-end: 6px;
`;

export const MobileLanguageSwitcher = styled.div`
  color: ${({ theme }) => theme.text.color.default};
  font-weight: ${({ theme }) => theme.font.weight.bold};
`;

export const MobileLanguageSwitchBtn = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing.xsmall.default};
  width: 100%;
  line-height: 20px;
  font-size: 15px;
  padding: 11px ${({ theme }) => theme.spacing.xsmall.default};
`;

export const MoreToolsMenuItem = styled(TextButton)`
  display: flex;
  padding: ${({ theme }) => theme.spacing.xsmall.default};
  color: ${({ theme }) => theme.nav.link.color};
  text-decoration: ${({ theme }) => theme.nav.link.textDecoration};
  width: 100%;
  align-items: center;
  border-radius: 5px;

  &:focus-visible,
  &:active {
    color: ${({ theme }) => theme.nav.link.color};
  }

  ${navLinkIconMixin};
  ${focusNavElementMixin};
`;

export const MoreToolsGroup = styled.div`
  & h5 {
    color: #bfbfbf;
  }
`;

export const MoreToolsItem = styled(NavLink)`
  display: flex;
  margin: 11px;
`;

export const MobileNavSheetInnerContainer = styled.div<{
  hasInnerPadding: boolean;
}>`
  height: 100%;
  width: 77vw;
  ${({ hasInnerPadding, theme }) =>
    hasInnerPadding &&
    css`
      padding: ${theme.spacing.xsmall.default};
    `}
`;

export const SideGroupMobileDivider = styled(Divider)`
  margin: ${({ theme }) => theme.spacing.xsmall.default} 0;
  border-bottom: 2px solid ${({ theme }) => theme.color.gray[700]};
`;

export const ExpandedListGroupTitle = styled.div`
  font-size: 12px;
  line-height: 18px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.color.inverted};
  padding-inline: ${({ theme }) => theme.spacing.xsmall.default};
  padding-top: 10px;
  padding-bottom: 10px;
`;
